import { Detection } from '../models/detection';
import { DetectionStatus } from '../enums/detection';
import { DetectionFilterKeys } from '../enums/detection-filter';
import { EmergencyTemplate } from '../models/emergency-template';
import { SiteDetectionLabel } from '../models/site-detection-label';

export type ConfidenceRange = { start: number; end: number }; // en % [0..100]

export type DetectionFilters = Partial<{
  [DetectionFilterKeys.LABELS]: string[];
  [DetectionFilterKeys.CREATE_MODES]: string[];     // ou number[] si tu préfères
  [DetectionFilterKeys.STATUS]: string[];           // codes en string
  [DetectionFilterKeys.CONFIDENCES]: ConfidenceRange;
}>;

export class DetectionTools {

  static applyFilters(items: Detection[], filters?: DetectionFilters): Detection[] {
    if (!items) return [];
    if (!filters) return items;

    let filtered = items;

    // -------------------------
    // LABELS
    // -------------------------
    const labels = filters[DetectionFilterKeys.LABELS];
    if (labels) {
      if (labels.length === 0) return []; // ✔ solution A
      filtered = filtered.filter(d =>
        labels.includes(d.label)
      );
    }

    // -------------------------
    // CREATE MODES
    // -------------------------
    const createModes = filters[DetectionFilterKeys.CREATE_MODES];
    if (createModes) {
      if (createModes.length === 0) return []; // ✔ solution A
      filtered = filtered.filter(d =>
        createModes.includes(String(d.createMode))
      );
    }

    // -------------------------
    // CONFIDENCES (range en %)
    // -------------------------
    const conf = filters[DetectionFilterKeys.CONFIDENCES];
    if (conf) {
      const min = conf.start / 100.0;
      const max = conf.end / 100.0;
      filtered = filtered.filter(d =>
        min <= d.confidence && d.confidence <= max
      );
    }

    // -------------------------
    // STATUS
    // -------------------------
    const statuses = filters[DetectionFilterKeys.STATUS];
    if (statuses) {
      if (statuses.length === 0) return []; // ✔ solution A
      filtered = filtered.filter(d => {
        if (d.emergency)
          return statuses.includes(String(d.emergency.level));

        if (d.status === DetectionStatus.TO_BE_CONFIRMED)
          return statuses.includes('0');

        if (d.status === DetectionStatus.CONFIRMED)
          return statuses.includes('-1');

        return false;
      });
    }

    return filtered;
  }


  static buildDefaultFilters(
    detections: Detection[],
    emergencyTpl: EmergencyTemplate,
    siteDetectionLabels: SiteDetectionLabel[]
  ): DetectionFilters {

    const filters: DetectionFilters = {};

    // -------------------------
    // LABELS (depuis SiteDetectionLabels.name)
    // -> on coche toutes les labels définies par les siteDetectionLabels
    // Fallback: labels rencontrés dans detections
    // -------------------------
    const siteDetectionLabelNames =
      (siteDetectionLabels ?? [])
        .map(l => l.name)
        .filter(Boolean);

    const detectionLabelValues =
      (detections ?? [])
        .map(d => d.label)
        .filter(Boolean);

    filters[DetectionFilterKeys.LABELS] = Array.from(new Set([
      ...siteDetectionLabelNames,
      ...detectionLabelValues
    ]));

    // -------------------------
    // CREATE MODES (depuis detections)
    // -------------------------
    filters[DetectionFilterKeys.CREATE_MODES] = Array.from(
      new Set((detections ?? []).map(d => String(d.createMode)).filter(v => v != null))
    );

    // -------------------------
    // STATUS / EMERGENCIES (depuis EmergencyTemplate.labels.level)
    // + états système 0 et -1
    // -------------------------
    const statusSet = new Set<string>();

    (emergencyTpl?.labels ?? []).forEach(l => statusSet.add(String(l.level)));
    statusSet.add('0');   // to_confirm
    statusSet.add('-1');  // confirmed

    filters[DetectionFilterKeys.STATUS] = Array.from(statusSet);

    // -------------------------
    // CONFIDENCES (range en %)
    // start=0, end=max réel (ou 100 si aucune donnée)
    // -------------------------
    const maxConfidence =
      (detections?.length ?? 0) > 0
        ? Math.max(...detections.map(d => d.confidence ?? 0))
        : 1;

    filters[DetectionFilterKeys.CONFIDENCES] = {
      start: 0,
      end: Math.ceil(maxConfidence * 100),
    };

    return filters;
  }
}

